import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, X, Eye, AlertTriangle, Mail, Pencil, Save, XCircle, CheckCircle2,
  Bold, Italic, Underline, Strikethrough, Link2, Link2Off, Baseline,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { interspireApi } from '../services/api';
import { handlePlainTextPaste } from '../utils/htmlDocument';
import DarkModeToggle from '../components/DarkModeToggle';
import { useAuth } from '../context/AuthContext';

/** Keep formatting tags/attrs that execCommand produces (color, links). */
function sanitizeEmailHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ['style', 'color', 'target', 'rel'],
    ADD_TAGS: ['font'],
  });
}

type FormatCmd = 'bold' | 'italic' | 'underline' | 'strikeThrough';

function ToolbarBtn({
  title,
  onClick,
  children,
  active,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      // Keep selection in the contentEditable when clicking the toolbar
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`inline-flex items-center justify-center h-8 w-8 rounded-md text-sm transition-colors ${
        active
          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600'
      }`}
    >
      {children}
    </button>
  );
}

export default function SendPreview() {
  const { statid: statidParam, jobid: jobidParam } = useParams<{ statid?: string; jobid?: string }>();
  const statid = statidParam ? parseInt(statidParam, 10) : NaN;
  const jobid  = jobidParam  ? parseInt(jobidParam,  10) : NaN;
  const isQueuePreview = !isNaN(jobid);

  const { canAccess } = useAuth();
  const canEdit = isQueuePreview && canAccess('queueEditEmail');

  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState('');
  const [newsletterid, setNewsletterid] = useState<number | null>(null);
  const [subject, setSubject]           = useState<string | null>(null);
  const [htmlBody, setHtmlBody]         = useState<string | null>(null);
  const [textBody, setTextBody]         = useState<string | null>(null);
  const [view, setView]                 = useState<'html' | 'text'>('html');

  // Edit mode state
  const [isEditing, setIsEditing]       = useState(false);
  const [editSubject, setEditSubject]   = useState('');
  const [saveStatus, setSaveStatus]     = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError]       = useState<string | null>(null);
  const editorRef                       = useRef<HTMLDivElement>(null);
  const iframeRef                       = useRef<HTMLIFrameElement>(null);

  // Link editor modal
  const [linkOpen, setLinkOpen]         = useState(false);
  const [linkUrl, setLinkUrl]           = useState('https://');
  const [linkError, setLinkError]       = useState<string | null>(null);
  const savedRangeRef                   = useRef<Range | null>(null);

  useEffect(() => {
    if (isQueuePreview ? isNaN(jobid) : isNaN(statid)) {
      setError('Invalid ID');
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = isQueuePreview
          ? await interspireApi.getQueuePreview(jobid)
          : await interspireApi.getSendPreview(statid);
        setCampaignName(res.data.campaignName);
        setNewsletterid(res.data.newsletterid);
        setSubject(res.data.subject);
        setHtmlBody(res.data.htmlBody);
        setTextBody(res.data.textBody);
      } catch (err: any) {
        setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    })();
  }, [statid, jobid, isQueuePreview]);

  // Populate the contentEditable editor when entering edit mode
  useEffect(() => {
    if (isEditing && editorRef.current && htmlBody != null) {
      editorRef.current.innerHTML = sanitizeEmailHtml(htmlBody);
    }
  }, [isEditing, htmlBody]);

  const handleClose = () => window.close();

  const enterEdit = () => {
    setEditSubject(subject ?? '');
    setSaveStatus('idle');
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setSaveStatus('idle');
    setSaveError(null);
    setLinkOpen(false);
  };

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const runFormat = (cmd: FormatCmd) => {
    focusEditor();
    document.execCommand(cmd, false);
  };

  const runForeColor = (color: string) => {
    focusEditor();
    document.execCommand('foreColor', false, color);
  };

  const captureSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    } else {
      savedRangeRef.current = null;
    }
  };

  const restoreSelection = () => {
    const range = savedRangeRef.current;
    if (!range) return false;
    const sel = window.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  };

  const openLinkEditor = () => {
    captureSelection();
    const sel = window.getSelection();
    let existing = 'https://';
    if (sel && sel.rangeCount > 0) {
      let node: Node | null = sel.anchorNode;
      while (node && node !== editorRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'A') {
          existing = (node as HTMLAnchorElement).getAttribute('href') || 'https://';
          break;
        }
        node = node.parentNode;
      }
    }
    setLinkUrl(existing);
    setLinkError(null);
    setLinkOpen(true);
  };

  const applyLink = () => {
    const raw = linkUrl.trim();
    if (!raw) {
      setLinkError('Enter a URL.');
      return;
    }
    let href = raw;
    if (!/^(https?:\/\/|mailto:|tel:|#|%%)/i.test(href) && !href.startsWith('{{')) {
      href = `https://${href}`;
    }
    focusEditor();
    if (!restoreSelection()) {
      setLinkError('Select text in the email first, then apply the link.');
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) {
      setLinkError('Select text in the email first, then apply the link.');
      return;
    }
    document.execCommand('createLink', false, href);
    // Soften target=_blank for email clients that support it
    const anchors = editorRef.current?.querySelectorAll(`a[href="${CSS.escape(href)}"]`);
    anchors?.forEach((a) => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
    setLinkOpen(false);
    setLinkError(null);
  };

  const removeLink = () => {
    focusEditor();
    document.execCommand('unlink', false);
  };

  const handleSave = useCallback(async () => {
    if (!newsletterid) return;
    const newHtml = editorRef.current
      ? sanitizeEmailHtml(editorRef.current.innerHTML)
      : (htmlBody ?? '');

    setSaveStatus('saving');
    setSaveError(null);
    try {
      await interspireApi.saveNewsletterContent(newsletterid, {
        subject: editSubject,
        htmlBody: newHtml,
      });
      // Commit changes to local state so preview reflects edits
      setSubject(editSubject);
      setHtmlBody(newHtml);
      setSaveStatus('saved');
      setIsEditing(false);
      setLinkOpen(false);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error ?? 'Failed to save — please try again');
      setSaveStatus('error');
    }
  }, [newsletterid, editSubject, htmlBody]);

  const hasHtml = !!htmlBody;
  const hasText = !!textBody;

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-slate-900 flex flex-col">

      {/* Slim standalone header */}
      <header className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 shadow-sm shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-4">

          {/* Left: Close */}
          <button
            onClick={handleClose}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
            Close
          </button>

          {/* Center: icon + title */}
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-indigo-600 flex items-center justify-center shrink-0">
              {isEditing ? <Pencil className="h-4 w-4 text-white" /> : <Eye className="h-4 w-4 text-white" />}
            </div>
            <span className="text-base font-bold text-gray-900 dark:text-white tracking-tight">
              {isEditing ? 'Edit Email' : 'Email Preview'}
            </span>
          </div>

          {/* Right: controls */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Save status badge */}
            {saveStatus === 'saved' && !isEditing && (
              <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5" /> Saved
              </span>
            )}

            {/* HTML / Text toggle — only in preview mode */}
            {!isEditing && !loading && !error && (hasHtml || hasText) && (
              <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-slate-600 text-xs font-medium">
                {hasHtml && (
                  <button
                    onClick={() => setView('html')}
                    className={`px-3 py-1.5 transition-colors ${
                      view === 'html'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600'
                    }`}
                  >
                    HTML
                  </button>
                )}
                {hasText && (
                  <button
                    onClick={() => setView('text')}
                    className={`px-3 py-1.5 transition-colors ${
                      view === 'text'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600'
                    }`}
                  >
                    Plain Text
                  </button>
                )}
              </div>
            )}

            {/* Edit / Save / Cancel buttons */}
            {!loading && !error && canEdit && !isEditing && (
              <button
                onClick={enterEdit}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={cancelEdit}
                  disabled={saveStatus === 'saving'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-500 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                >
                  {saveStatus === 'saving'
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Save className="h-3.5 w-3.5" />}
                  {saveStatus === 'saving' ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Loading */}
      {loading && (
        <div className="flex-1 flex items-center justify-center gap-2 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading preview…</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex-1 flex items-start justify-center pt-16 px-4">
          <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl px-5 py-4 text-sm text-red-700 dark:text-red-400 max-w-lg">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-0.5">Could not load preview</p>
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <div className="flex-1 flex flex-col">

          {/* Campaign name + subject bar */}
          <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-6 py-3">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-snug">
              {campaignName}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Subject:</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  className="flex-1 text-sm border border-amber-400 rounded px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">{subject ?? '—'}</span>
              )}
            </div>
            {saveError && (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />{saveError}
              </div>
            )}
            <div className="mt-0.5 text-xs text-gray-400 dark:text-slate-500">
              {isQueuePreview
                ? <>Queue Job ID: <span className="font-mono">{jobid}</span>{newsletterid ? <> · Newsletter ID: <span className="font-mono">{newsletterid}</span></> : null}</>
                : <>Send ID: <span className="font-mono">{statid}</span></>
              }
            </div>
          </div>

          {/* Edit mode — contentEditable HTML editor */}
          {isEditing && (
            <div className="flex-1 flex flex-col bg-gray-200 dark:bg-slate-950 p-4 overflow-auto">
              <div className="w-full max-w-3xl mx-auto flex-1 flex flex-col">
                <div className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                  <Pencil className="h-3.5 w-3.5" />
                  Editing HTML — changes will be saved directly to Interspire
                </div>

                {/* Formatting toolbar */}
                <div className="mb-2 flex flex-wrap items-center gap-0.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-slate-800 px-2 py-1.5 shadow-sm">
                  <ToolbarBtn title="Bold" onClick={() => runFormat('bold')}>
                    <Bold className="h-4 w-4" />
                  </ToolbarBtn>
                  <ToolbarBtn title="Italic" onClick={() => runFormat('italic')}>
                    <Italic className="h-4 w-4" />
                  </ToolbarBtn>
                  <ToolbarBtn title="Underline" onClick={() => runFormat('underline')}>
                    <Underline className="h-4 w-4" />
                  </ToolbarBtn>
                  <ToolbarBtn title="Strikethrough" onClick={() => runFormat('strikeThrough')}>
                    <Strikethrough className="h-4 w-4" />
                  </ToolbarBtn>

                  <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-slate-600" aria-hidden />

                  <ToolbarBtn title="Text color: black" onClick={() => runForeColor('#000000')}>
                    <span className="relative inline-flex">
                      <Baseline className="h-4 w-4" />
                      <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 rounded bg-black" />
                    </span>
                  </ToolbarBtn>
                  <ToolbarBtn title="Text color: red" onClick={() => runForeColor('#ff0000')}>
                    <span className="relative inline-flex text-red-600">
                      <Baseline className="h-4 w-4" />
                      <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 rounded bg-red-600" />
                    </span>
                  </ToolbarBtn>

                  <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-slate-600" aria-hidden />

                  <ToolbarBtn title="Add / edit link" onClick={openLinkEditor}>
                    <Link2 className="h-4 w-4" />
                  </ToolbarBtn>
                  <ToolbarBtn title="Remove link" onClick={removeLink}>
                    <Link2Off className="h-4 w-4" />
                  </ToolbarBtn>
                </div>

                <div className="flex-1 bg-white dark:bg-slate-800 shadow-lg rounded-lg overflow-hidden border-2 border-amber-400 dark:border-amber-600">
                  <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    className="w-full min-h-[600px] p-4 focus:outline-none text-sm font-sans leading-relaxed"
                    style={{ wordWrap: 'break-word' }}
                    onPaste={handlePlainTextPaste}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Preview mode — HTML iframe */}
          {!isEditing && view === 'html' && hasHtml && (
            <div className="flex-1 flex justify-center bg-gray-200 dark:bg-slate-950 p-4 overflow-auto">
              <div className="w-full max-w-3xl bg-white shadow-lg rounded-lg overflow-hidden">
                <iframe
                  ref={iframeRef}
                  title="Email HTML Preview"
                  sandbox="allow-same-origin allow-popups"
                  srcDoc={htmlBody!}
                  className="w-full"
                  style={{ minHeight: '600px', height: '100%', border: 'none', display: 'block' }}
                  onLoad={() => {
                    const iframe = iframeRef.current;
                    if (iframe?.contentDocument?.body) {
                      const h = iframe.contentDocument.body.scrollHeight;
                      if (h > 0) iframe.style.height = `${h + 32}px`;
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* HTML not available */}
          {!isEditing && view === 'html' && !hasHtml && (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              No HTML body stored for this send.
            </div>
          )}

          {/* Plain text view */}
          {!isEditing && view === 'text' && hasText && (
            <div className="flex-1 flex justify-center bg-gray-200 dark:bg-slate-950 p-4 overflow-auto">
              <div className="w-full max-w-3xl bg-white dark:bg-slate-800 shadow-lg rounded-lg p-6">
                <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                  {textBody}
                </pre>
              </div>
            </div>
          )}

          {/* Plain text not available */}
          {!isEditing && view === 'text' && !hasText && (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              No plain-text body stored for this send.
            </div>
          )}
        </div>
      )}

      {/* Link editor modal */}
      {linkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-600 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Link2 className="h-5 w-5 text-blue-600" />
                Edit link
              </h2>
              <button
                type="button"
                onClick={() => setLinkOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Select text in the email first, then set the URL.
              </p>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                URL
                <input
                  type="text"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyLink();
                    }
                  }}
                  autoFocus
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                  placeholder="https://…"
                />
              </label>
              {linkError && (
                <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {linkError}
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setLinkOpen(false)}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-500 rounded-md text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyLink}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Apply link
              </button>
            </div>
          </div>
        </div>
      )}

      <DarkModeToggle />
    </div>
  );
}
